import { useState, useEffect, useCallback } from 'react';
import { DollarSign, Download, TrendingUp, Users, Zap, ChevronRight, X } from 'lucide-react';
import { authenticatedFetch } from '../../lib/auth';

interface MemberSummary {
  member_id: string | null;
  member_name: string;
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  unique_users: number;
}

interface EndpointSummary {
  endpoint: string;
  calls: number;
  total_tokens: number;
  total_cost_usd: number;
}

interface DaySummary {
  day: string;
  calls: number;
  total_tokens: number;
  total_cost_usd: number;
}

interface MemberDetail {
  member: { id: string; organization_name: string; contact_email: string } | null;
  total: { calls: number; total_tokens: number; total_cost_usd: number; unique_users: number };
  by_user: { user_id: string; user_email: string; calls: number; total_tokens: number; total_cost_usd: number }[];
  by_month: { month: string; calls: number; total_tokens: number; total_cost_usd: number }[];
  by_endpoint: { endpoint: string; calls: number; total_tokens: number; total_cost_usd: number }[];
}

const ENDPOINT_LABELS: Record<string, string> = {
  // Studieplan-KI
  ai_generate_study_plan: 'Generer studieplan',
  ai_improve_study_plan: 'Forbedre studieplan',
  ai_validate_study_plan: 'Validere studieplan',
  ai_translate_study_plan: 'Oversett studieplan (NO→EN)',
  ai_translate_text: 'Oversett tekst',

  // Moduler og litteratur
  ai_suggest_modules: 'Foreslå moduler',
  ai_suggest_literature: 'Foreslå litteratur',
  ai_module_literature: 'Foreslå litteratur per modul',

  // ESCO-relatert
  ai_suggest_esco_skills: 'ESCO-forslag på utbytter',
  ai_esco_query_enhance: 'ESCO: utvide søkeord',
  ai_esco_query_enhance_batch: 'ESCO: utvide søkeord (batch)',
  ai_esco_single_rerank: 'ESCO: kvalitetsrangering',
  ai_esco_batch_rerank: 'ESCO: kvalitetsrangering (batch)',
  ai_esco_quality_validation: 'ESCO: kvalitetssjekk av koblinger',

  // Eksamen
  ai_generate_exam: 'Generer eksamen',
  ai_generate_questions_from_content: 'Lag spørsmål fra innhold',
  ai_grade_exam: 'Vurder eksamensbesvarelse',
  ai_grade_exam_answer: 'Vurder enkelt eksamenssvar',

  // Interne støttekall (lagt til 2026-10-02 for komplett sporing)
  ai_pre_analyze_study_plan: 'Pre-analyse (del av generering)',
  ai_compress_outcomes: 'Kompresjon av læringsutbytter',
  ai_bloom_classify: 'Bloom-klassifisering',
  ai_literature_query_generation: 'Litteratursøk: generer spørringer',
  ai_literature_rerank: 'Litteratursøk: re-rangering',
  ai_literature_relevance_check: 'Litteratur: relevanssjekk',
};

// Subsidierte endepunkter — vises med 0 NOK i rapport for medlem,
// men systemkostnaden spores for systemadmin.
const SUBSIDIZED_ENDPOINTS = new Set([
  'ai_validate_study_plan',
  'ai_esco_quality_validation'
]);

const USD_TO_NOK = 11; // Cirka konverteringskurs — kan flyttes til config senere

function fmtNum(n: number): string {
  return n.toLocaleString('nb-NO');
}
function fmtNok(usd: number): string {
  return (usd * USD_TO_NOK).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';
}
function fmtUsd(usd: number): string {
  return '$' + usd.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}

function fromDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

export default function TokenUsage() {
  const [summary, setSummary] = useState<{
    total: { calls: number; total_tokens: number; total_cost_usd: number };
    by_member: MemberSummary[];
    by_endpoint: EndpointSummary[];
    by_day: DaySummary[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [drilldownMember, setDrilldownMember] = useState<string | null>(null);
  const [memberDetail, setMemberDetail] = useState<MemberDetail | null>(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const from = fromDaysAgo(days);
      const res = await authenticatedFetch(`/api/admin/token-usage/summary?from=${from}`);
      const data = await res.json();
      setSummary(data);
    } catch (err) {
      console.error('Load summary failed:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  useEffect(() => {
    if (!drilldownMember) { setMemberDetail(null); return; }
    const from = fromDaysAgo(days);
    authenticatedFetch(`/api/admin/token-usage/by-member/${drilldownMember}?from=${from}`)
      .then(r => r.json())
      .then(setMemberDetail);
  }, [drilldownMember, days]);

  async function handleExport(memberId?: string) {
    const from = fromDaysAgo(days);
    const url = memberId
      ? `/api/admin/token-usage/export.csv?from=${from}&member_id=${memberId}`
      : `/api/admin/token-usage/export.csv?from=${from}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) { alert('Eksport feilet'); return; }
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `token-usage-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign size={22} className="text-kf-primary" />
            KI-tokenforbruk og kostnader
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Oversikt over OpenAI-tokenforbruk per medlemsorganisasjon. Data bevares 5 år for regnskapsformål.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm">
            <option value={7}>Siste 7 dager</option>
            <option value={30}>Siste 30 dager</option>
            <option value={90}>Siste 90 dager</option>
            <option value={365}>Siste år</option>
            <option value={730}>Siste 2 år</option>
            <option value={1825}>Siste 5 år</option>
          </select>
          <button onClick={() => handleExport()} className="flex items-center gap-2 px-3 py-2 bg-kf-primary text-white rounded-md text-sm hover:bg-kf-primary-dark">
            <Download size={16} /> Eksporter CSV
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-12 text-gray-500">Laster…</div>}

      {!loading && summary && (
        <>
          {/* Totalsum */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
            <SumCard icon={<Zap size={18} />} label="Antall KI-kall" value={fmtNum(summary.total.calls)} />
            <SumCard icon={<TrendingUp size={18} />} label="Totalt tokens" value={fmtNum(summary.total.total_tokens || 0)} />
            <SumCard icon={<DollarSign size={18} />} label="Kostnad (USD)" value={fmtUsd(Number(summary.total.total_cost_usd || 0))} />
            <SumCard icon={<DollarSign size={18} />} label="Estimert NOK" value={fmtNok(Number(summary.total.total_cost_usd || 0))} highlight />
          </div>

          {/* Per medlem */}
          <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Users size={18} /> Per medlemsorganisasjon
          </h3>
          <div className="border rounded-md overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left">Medlem</th>
                  <th className="px-3 py-2 text-right">Brukere</th>
                  <th className="px-3 py-2 text-right">Kall</th>
                  <th className="px-3 py-2 text-right">Tokens</th>
                  <th className="px-3 py-2 text-right">Kostnad (USD)</th>
                  <th className="px-3 py-2 text-right">Estimert NOK</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {summary.by_member.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">Ingen KI-aktivitet i valgt periode.</td></tr>
                )}
                {summary.by_member.map(m => (
                  <tr key={m.member_id || 'unknown'} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{m.member_name}</td>
                    <td className="px-3 py-2 text-right">{m.unique_users}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(m.calls)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(m.total_tokens)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(Number(m.total_cost_usd))}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-kf-primary">{fmtNok(Number(m.total_cost_usd))}</td>
                    <td className="px-3 py-2 text-right">
                      {m.member_id && (
                        <button onClick={() => setDrilldownMember(m.member_id)} className="text-xs text-kf-primary hover:underline flex items-center gap-1">
                          Detaljer <ChevronRight size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Per endpoint */}
          <h3 className="text-base font-semibold text-gray-900 mb-2">Per KI-funksjon</h3>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left">Funksjon</th>
                  <th className="px-3 py-2 text-right">Kall</th>
                  <th className="px-3 py-2 text-right">Tokens</th>
                  <th className="px-3 py-2 text-right">Kostnad</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_endpoint.map(e => {
                  const subsidized = SUBSIDIZED_ENDPOINTS.has(e.endpoint);
                  return (
                    <tr key={e.endpoint} className="border-b">
                      <td className="px-3 py-2">
                        {ENDPOINT_LABELS[e.endpoint] || e.endpoint}
                        {subsidized && (
                          <span className="ml-2 inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                            Dekket av KF
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">{fmtNum(e.calls)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtNum(e.total_tokens)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${subsidized ? 'text-emerald-700 font-medium' : ''}`}>
                        {subsidized ? '0 kr' : fmtNok(Number(e.total_cost_usd))}
                        {subsidized && (
                          <span className="block text-xs text-gray-400 font-normal">
                            (systemkost: {fmtNok(Number(e.total_cost_usd))})
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Drilldown-modal */}
      {drilldownMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-semibold">{memberDetail?.member?.organization_name || 'Detaljer'}</h3>
                {memberDetail?.member?.contact_email && (
                  <div className="text-xs text-gray-500">{memberDetail.member.contact_email}</div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleExport(drilldownMember)} className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded">
                  <Download size={12} /> CSV
                </button>
                <button onClick={() => setDrilldownMember(null)} className="text-gray-500 hover:text-gray-700">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-4">
              {memberDetail ? (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <SumCard icon={<Zap size={16} />} label="KI-kall" value={fmtNum(memberDetail.total.calls)} />
                    <SumCard icon={<TrendingUp size={16} />} label="Tokens" value={fmtNum(memberDetail.total.total_tokens || 0)} />
                    <SumCard icon={<DollarSign size={16} />} label="Estimert NOK" value={fmtNok(Number(memberDetail.total.total_cost_usd || 0))} highlight />
                  </div>

                  <h4 className="text-sm font-semibold mb-2">Per måned</h4>
                  <div className="border rounded mb-4 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Måned</th>
                          <th className="px-3 py-1.5 text-right">Kall</th>
                          <th className="px-3 py-1.5 text-right">Tokens</th>
                          <th className="px-3 py-1.5 text-right">NOK (est.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberDetail.by_month.map(m => (
                          <tr key={m.month} className="border-t">
                            <td className="px-3 py-1.5">{m.month}</td>
                            <td className="px-3 py-1.5 text-right">{fmtNum(m.calls)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(m.total_tokens)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{fmtNok(Number(m.total_cost_usd))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <h4 className="text-sm font-semibold mb-2">Per bruker</h4>
                  <div className="border rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Bruker</th>
                          <th className="px-3 py-1.5 text-right">Kall</th>
                          <th className="px-3 py-1.5 text-right">Tokens</th>
                          <th className="px-3 py-1.5 text-right">NOK (est.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberDetail.by_user.map(u => (
                          <tr key={u.user_id || u.user_email} className="border-t">
                            <td className="px-3 py-1.5">{u.user_email}</td>
                            <td className="px-3 py-1.5 text-right">{fmtNum(u.calls)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(u.total_tokens)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{fmtNok(Number(u.total_cost_usd))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">Laster…</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SumCard({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-3 rounded-md border ${highlight ? 'bg-kf-primary/5 border-kf-primary/20' : 'bg-gray-50 border-gray-200'}`}>
      <div className={`flex items-center gap-2 text-xs ${highlight ? 'text-kf-primary' : 'text-gray-600'}`}>
        {icon}
        <span>{label}</span>
      </div>
      <div className={`text-lg font-bold mt-1 ${highlight ? 'text-kf-primary' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
